import type { Store } from 'redux'

import type { Action, SentinelAction } from '../interfaces.js'
import { get } from '../utils/js_utils.js'
import type { State as DirtyHandlerIdsState } from './dirtyHandlerIds.js'
import { reduce as dirtyHandlerIds } from './dirtyHandlerIds.js'
import type { State as DragOffsetState } from './dragOffset.js'
import { reduce as dragOffset } from './dragOffset.js'
import type { State as DragOperationState } from './dragOperation.js'
import { reduce as dragOperation } from './dragOperation.js'
import type { State as DropSettlingState } from './dropSettling.js'
import { reduce as dropSettling } from './dropSettling.js'
import type { State as RefCountState } from './refCount.js'
import { reduce as refCount } from './refCount.js'
import type { State as StateIdState } from './stateId.js'
import { reduce as stateId } from './stateId.js'

export interface State {
	dirtyHandlerIds: DirtyHandlerIdsState
	dragOffset: DragOffsetState
	refCount: RefCountState
	dragOperation: DragOperationState
	dropSettling: DropSettlingState
	stateId: StateIdState
}

export function reduce(state: State = {} as State, action: Action<any>): State {
	return {
		dirtyHandlerIds: dirtyHandlerIds(state.dirtyHandlerIds, {
			type: action.type,
			payload: {
				...action.payload,
				prevTargetIds: get<string[]>(state, 'dragOperation.targetIds', []),
			},
		}),
		dragOffset: dragOffset(state.dragOffset, action),
		refCount: refCount(state.refCount, action),
		dragOperation: dragOperation(state.dragOperation, action),
		dropSettling: dropSettling(state.dropSettling, action),
		stateId: stateId(state.stateId),
	}
}

/**
 * Everything dnd-core dispatches. Most actions carry a payload; `endDrag` and
 * `publishDragSource` are bare sentinels.
 */
export type DndAction = Action<any> | SentinelAction

/**
 * The dnd-core redux store.
 *
 * Explicitly parameterized on `DndAction` rather than letting Redux default to
 * `UnknownAction`: that default carries an `[extraProps: string]: unknown` index
 * signature, which dnd-core's tightly-shaped actions do not (and should not)
 * have.
 */
export type DndStore = Store<State, DndAction>
